// src/pages/DepartmentFrontend.jsx
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { collection, getDocs, doc, getDoc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase-config';

const DepartmentFrontend = () => {
  const [departments, setDepartments] = useState([]);
  const [selectedDept, setSelectedDept] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [facultyList, setFacultyList] = useState([]);
  const [stats, setStats] = useState({
    totalDepartments: 0,
    totalFaculty: 0,
    totalSeats: 0
  });

  useEffect(() => {
    // Load faculty data
    const facultyUnsubscribe = onSnapshot(collection(db, 'faculty'), (snapshot) => {
      const facultyData = [];
      snapshot.forEach((doc) => {
        facultyData.push({ id: doc.id, ...doc.data() });
      });
      setFacultyList(facultyData);
    });

    const deptUnsubscribe = onSnapshot(collection(db, 'departments'), (snapshot) => {
      const deptData = [];
      snapshot.forEach((doc) => {
        deptData.push({ id: doc.id, ...doc.data() });
      });
      setDepartments(deptData);
      
      // Calculate stats
      let totalFaculty = 0;
      let totalSeats = 0;
      deptData.forEach(dept => {
        totalFaculty += parseInt(dept.facultyCount) || 0;
        totalSeats += parseInt(dept.seats) || 0;
      });
      setStats({
        totalDepartments: deptData.length,
        totalFaculty,
        totalSeats
      });
      
      setLoading(false);
    });

    return () => {
      facultyUnsubscribe();
      deptUnsubscribe();
    };
  }, []);

  const handleDepartmentSelect = (dept) => {
    setSelectedDept(dept);
    setActiveTab('overview');
  };

  const getFacultyCount = (dept) => {
    return dept.facultyCount || 'N/A';
  };

  const getSeats = (dept) => {
    return dept.seats || 'N/A';
  };

  const getDuration = (dept) => {
    return dept.duration || 'N/A';
  };

  // Get HOD details
  const getHODDetails = (hodId) => {
    if (!hodId) return null;
    return facultyList.find(f => f.id === hodId) || null;
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <div style={{ 
          width: '50px', 
          height: '50px', 
          borderRadius: '50%', 
          border: '4px solid rgba(14,165,233,0.1)', 
          borderTopColor: 'var(--clr-blue)', 
          animation: 'spin 0.8s linear infinite' 
        }}></div>
      </div>
    );
  }

  return (
    <div className="container departments-frontend">
      <motion.h1
        className="page-title"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <i className="fas fa-building" style={{ color: 'var(--clr-blue)' }}></i> Our Departments
      </motion.h1>
      <p className="page-subtitle">
        Explore our academic departments dedicated to excellence in medical sciences and technology education.
      </p>

      {/* Stats */}
      <div className="stats-grid">
        <div className="stat-card stat-blue">
          <div className="stat-icon"><i className="fas fa-building"></i></div>
          <div className="stat-value">{stats.totalDepartments}</div>
          <div className="stat-label">Departments</div>
        </div>
        <div className="stat-card stat-emerald">
          <div className="stat-icon"><i className="fas fa-user-tie"></i></div>
          <div className="stat-value">{stats.totalFaculty}+</div>
          <div className="stat-label">Faculty Members</div>
        </div>
        <div className="stat-card stat-amber">
          <div className="stat-icon"><i className="fas fa-users"></i></div>
          <div className="stat-value">{stats.totalSeats}+</div>
          <div className="stat-label">Total Seats</div>
        </div>
        <div className="stat-card stat-purple">
          <div className="stat-icon"><i className="fas fa-graduation-cap"></i></div>
          <div className="stat-value">{stats.totalDepartments * 3}+</div>
          <div className="stat-label">Programs Offered</div>
        </div>
      </div>

      {/* Department Selector */}
      <div className="dept-selector">
        {departments.map((dept) => (
          <button
            key={dept.id}
            className={`dept-btn ${selectedDept?.id === dept.id ? 'active' : ''}`}
            onClick={() => handleDepartmentSelect(dept)}
          >
            <i className="fas fa-building"></i> {dept.name}
          </button>
        ))}
        {departments.length === 0 && (
          <div className="empty-message">No departments available</div>
        )}
      </div>

      {/* Department Detail View */}
      {selectedDept ? (
        <motion.div
          className="dept-detail"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          <div className="dept-header">
            <div className="dept-header-left">
              <div className="dept-icon-large">
                <i className="fas fa-building"></i>
              </div>
              <div>
                <h2>{selectedDept.name}</h2>
                <p className="dept-meta">
                  <span><i className="fas fa-user-tie"></i> {getFacultyCount(selectedDept)} Faculty</span>
                  <span><i className="fas fa-users"></i> {getSeats(selectedDept)} Seats</span>
                  <span><i className="fas fa-clock"></i> {getDuration(selectedDept)}</span>
                </p>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="dept-tabs">
            <button 
              className={activeTab === 'overview' ? 'active' : ''} 
              onClick={() => setActiveTab('overview')}
            >
              <i className="fas fa-info-circle"></i> Overview
            </button>
            <button 
              className={activeTab === 'faculty' ? 'active' : ''} 
              onClick={() => setActiveTab('faculty')}
            >
              <i className="fas fa-user-tie"></i> Faculty
            </button>
            <button 
              className={activeTab === 'notices' ? 'active' : ''} 
              onClick={() => setActiveTab('notices')}
            >
              <i className="fas fa-bullhorn"></i> Notices
            </button>
            <button 
              className={activeTab === 'labs' ? 'active' : ''} 
              onClick={() => setActiveTab('labs')}
            >
              <i className="fas fa-flask"></i> Labs
            </button>
            <button 
              className={activeTab === 'gallery' ? 'active' : ''} 
              onClick={() => setActiveTab('gallery')}
            >
              <i className="fas fa-images"></i> Gallery
            </button>
          </div>

          <div className="dept-content">
            {/* Overview Tab */}
            {activeTab === 'overview' && (
              <div className="tab-content">
                <div className="overview-grid">
                  <div className="overview-left">
                    {selectedDept.image && (
                      <img src={selectedDept.image} alt={selectedDept.name} className="dept-image" />
                    )}
                    <div className="dept-contact">
                      <h4><i className="fas fa-address-card"></i> Contact Information</h4>
                      {selectedDept.email && (
                        <p><i className="fas fa-envelope"></i> {selectedDept.email}</p>
                      )}
                      {selectedDept.phone && (
                        <p><i className="fas fa-phone"></i> {selectedDept.phone}</p>
                      )}
                      {selectedDept.established && (
                        <p><i className="fas fa-calendar-alt"></i> Established: {selectedDept.established}</p>
                      )}
                    </div>
                  </div>
                  <div className="overview-right">
                    <h3>About {selectedDept.name}</h3>
                    <p>{selectedDept.description || 'No description available.'}</p>
                    
                    {/* HOD Message with Picture */}
                    {selectedDept.hodMessage && (
                      <div className="hod-message">
                        <h4><i className="fas fa-quote-left"></i> Message from Head of Department</h4>
                        <div className="hod-message-box">
                          {(() => {
                            const hod = getHODDetails(selectedDept.headOfDepartment);
                            return (
                              <>
                                {hod && (
                                  <div className="hod-profile">
                                    <img 
                                      src={hod.image || 'https://ui-avatars.com/api/?name=' + hod.name + '&size=80&background=0ea5e9&color=fff'} 
                                      alt={hod.name} 
                                      className="hod-avatar-large"
                                    />
                                    <div className="hod-info">
                                      <h5>{hod.name}</h5>
                                      <span className="hod-designation">{hod.designation || 'Head of Department'}</span>
                                      {hod.email && (
                                        <span className="hod-contact"><i className="fas fa-envelope"></i> {hod.email}</span>
                                      )}
                                      {hod.phone && (
                                        <span className="hod-contact"><i className="fas fa-phone"></i> {hod.phone}</span>
                                      )}
                                    </div>
                                  </div>
                                )}
                                <div className="hod-message-text">
                                  <p>"{selectedDept.hodMessage}"</p>
                                </div>
                              </>
                            );
                          })()}
                        </div>
                      </div>
                    )}

                    {selectedDept.achievements && (
                      <div className="achievements">
                        <h4><i className="fas fa-trophy"></i> Achievements</h4>
                        <div className="achievement-tags">
                          {selectedDept.achievements.split(',').map((ach, idx) => (
                            <span key={idx} className="achievement-tag">{ach.trim()}</span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Faculty Tab */}
            {activeTab === 'faculty' && (
              <div className="tab-content">
                <div className="faculty-section">
                  <h3><i className="fas fa-user-tie"></i> Faculty Members</h3>
                  <p className="faculty-count">{getFacultyCount(selectedDept)} faculty members</p>
                  
                  {/* Show HOD in faculty list */}
                  {(() => {
                    const hod = getHODDetails(selectedDept.headOfDepartment);
                    const deptFaculty = facultyList.filter(f => f.department === selectedDept.name);
                    
                    return (
                      <div className="faculty-grid-full">
                        {hod && (
                          <div className="faculty-card-full hod-card-full">
                            <div className="faculty-card-header">
                              <i className="fas fa-crown" style={{ color: '#f59e0b' }}></i>
                              <span>Head of Department</span>
                            </div>
                            <div className="faculty-card-content">
                              <img 
                                src={hod.image || 'https://ui-avatars.com/api/?name=' + hod.name + '&size=80&background=0ea5e9&color=fff'} 
                                alt={hod.name} 
                              />
                              <div>
                                <h4>{hod.name}</h4>
                                <p>{hod.designation || 'Head of Department'}</p>
                                {hod.email && <span><i className="fas fa-envelope"></i> {hod.email}</span>}
                              </div>
                            </div>
                          </div>
                        )}
                        
                        {deptFaculty.length > 0 ? (
                          <div className="faculty-grid">
                            {deptFaculty.map((f) => (
                              <div className="faculty-mini-card" key={f.id}>
                                <img src={f.image || 'https://ui-avatars.com/api/?name=' + f.name + '&size=60&background=0ea5e9&color=fff'} alt={f.name} />
                                <h5>{f.name}</h5>
                                <span>{f.designation || 'Faculty'}</span>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="faculty-placeholder">
                            <i className="fas fa-users"></i>
                            <p>Faculty members will be displayed here</p>
                            <Link to="/faculty" className="view-all-link">View All Faculty →</Link>
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>
              </div>
            )}

            {/* Notices Tab */}
            {activeTab === 'notices' && (
              <div className="tab-content">
                <div className="notices-section">
                  <h3><i className="fas fa-bullhorn"></i> Notices & Announcements</h3>
                  {selectedDept.notices && selectedDept.notices.length > 0 ? (
                    <div className="notices-list">
                      {selectedDept.notices.map((notice) => (
                        <div className="notice-item" key={notice.id}>
                          <div className="notice-icon">
                            <i className="fas fa-file-pdf"></i>
                          </div>
                          <div className="notice-content">
                            <h4>{notice.title}</h4>
                            {notice.description && <p>{notice.description}</p>}
                            <span className="notice-date">
                              <i className="far fa-calendar-alt"></i> 
                              {new Date(notice.createdAt).toLocaleDateString()}
                            </span>
                          </div>
                          <a href={notice.fileUrl} target="_blank" rel="noopener noreferrer" className="notice-download">
                            <i className="fas fa-download"></i> View
                          </a>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="empty-state-mini">
                      <i className="fas fa-file-pdf"></i>
                      <p>No notices available for this department.</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Labs Tab */}
            {activeTab === 'labs' && (
              <div className="tab-content">
                <div className="labs-section">
                  <h3><i className="fas fa-flask"></i> Laboratories</h3>
                  {selectedDept.labs && selectedDept.labs.length > 0 ? (
                    <div className="labs-grid">
                      {selectedDept.labs.map((lab) => (
                        <div className="lab-card" key={lab.id}>
                          <div className="lab-images">
                            {lab.images && lab.images.slice(0, 3).map((img, idx) => (
                              <img key={idx} src={img} alt={lab.name} className="lab-image" />
                            ))}
                            {lab.images && lab.images.length > 3 && (
                              <div className="more-images">+{lab.images.length - 3}</div>
                            )}
                          </div>
                          <div className="lab-info">
                            <h4>{lab.name}</h4>
                            {lab.equipment && (
                              <p className="lab-equipment"><i className="fas fa-tools"></i> {lab.equipment}</p>
                            )}
                            {lab.description && <p>{lab.description}</p>}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="empty-state-mini">
                      <i className="fas fa-flask"></i>
                      <p>No labs available for this department.</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Gallery Tab */}
            {activeTab === 'gallery' && (
              <div className="tab-content">
                <div className="gallery-section">
                  <h3><i className="fas fa-images"></i> Department Gallery</h3>
                  {selectedDept.gallery && selectedDept.gallery.length > 0 ? (
                    <div className="gallery-grid">
                      {selectedDept.gallery.map((img) => (
                        <div className="gallery-item" key={img.id}>
                          <img src={img.image} alt="Department" />
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="empty-state-mini">
                      <i className="fas fa-images"></i>
                      <p>No gallery images available for this department.</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </motion.div>
      ) : (
        <div className="empty-state">
          <i className="fas fa-building"></i>
          <h3>Select a Department</h3>
          <p>Click on a department above to view its details, faculty, notices, labs, and gallery.</p>
        </div>
      )}

      {/* Add CSS */}
      <style>{`
        .departments-frontend {
          padding-top: 40px;
        }

        .page-title {
          font-family: 'Playfair Display', serif;
          font-size: 2.5rem;
          margin-bottom: 10px;
        }

        .page-subtitle {
          color: var(--text-secondary);
          margin-bottom: 30px;
          font-size: 1.05rem;
        }

        /* Stats */
        .stats-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 20px;
          margin-bottom: 40px;
        }

        .stat-card {
          background: rgba(255, 255, 255, 0.5);
          backdrop-filter: blur(20px) saturate(140%);
          border-radius: 16px;
          padding: 24px 20px;
          text-align: center;
          transition: all 0.3s ease;
          border-bottom: 3px solid var(--clr-blue);
        }

        .stat-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 12px 32px rgba(0, 0, 0, 0.1);
        }

        .stat-blue { border-bottom-color: var(--clr-blue); }
        .stat-emerald { border-bottom-color: var(--clr-emerald); }
        .stat-amber { border-bottom-color: var(--clr-amber); }
        .stat-purple { border-bottom-color: var(--clr-purple); }

        .stat-icon {
          width: 50px;
          height: 50px;
          border-radius: 14px;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 10px;
          font-size: 1.3rem;
          color: white;
        }

        .stat-blue .stat-icon { background: var(--clr-blue); }
        .stat-emerald .stat-icon { background: var(--clr-emerald); }
        .stat-amber .stat-icon { background: var(--clr-amber); }
        .stat-purple .stat-icon { background: var(--clr-purple); }

        .stat-value {
          font-family: 'Playfair Display', serif;
          font-size: 2rem;
          font-weight: 800;
        }

        .stat-blue .stat-value { color: var(--clr-blue); }
        .stat-emerald .stat-value { color: var(--clr-emerald); }
        .stat-amber .stat-value { color: var(--clr-amber); }
        .stat-purple .stat-value { color: var(--clr-purple); }

        .stat-label {
          font-size: 0.85rem;
          font-weight: 700;
          margin-top: 5px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        /* Department Selector */
        .dept-selector {
          display: flex;
          flex-wrap: wrap;
          gap: 12px;
          margin-bottom: 30px;
          padding: 16px;
          background: rgba(255, 255, 255, 0.3);
          backdrop-filter: blur(20px) saturate(140%);
          border-radius: 16px;
          border: 1px solid rgba(255, 255, 255, 0.6);
        }

        .dept-btn {
          padding: 10px 24px;
          background: rgba(255, 255, 255, 0.4);
          border: 2px solid rgba(14, 165, 233, 0.15);
          border-radius: 30px;
          cursor: pointer;
          font-weight: 700;
          font-size: 0.85rem;
          color: var(--text-secondary);
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .dept-btn:hover {
          background: rgba(255, 255, 255, 0.7);
          transform: translateY(-2px);
          border-color: var(--clr-blue);
        }

        .dept-btn.active {
          background: linear-gradient(135deg, var(--clr-blue), #0284c7);
          color: white;
          border-color: var(--clr-blue);
          box-shadow: 0 4px 15px rgba(14, 165, 233, 0.3);
        }

        .empty-message {
          color: var(--text-muted);
          padding: 10px 20px;
          font-weight: 600;
        }

        /* Department Detail */
        .dept-detail {
          background: rgba(255, 255, 255, 0.45);
          backdrop-filter: blur(20px) saturate(140%);
          border-radius: 24px;
          overflow: hidden;
          border: 1px solid rgba(255, 255, 255, 0.6);
          box-shadow: 0 8px 32px rgba(14, 165, 233, 0.06);
        }

        .dept-header {
          padding: 24px 32px;
          background: linear-gradient(135deg, rgba(14, 165, 233, 0.08), rgba(14, 165, 233, 0.02));
          border-bottom: 1px solid rgba(14, 165, 233, 0.1);
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
          gap: 16px;
        }

        .dept-header-left {
          display: flex;
          align-items: center;
          gap: 16px;
        }

        .dept-icon-large {
          width: 56px;
          height: 56px;
          border-radius: 12px;
          background: linear-gradient(135deg, var(--clr-blue), #0284c7);
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-size: 24px;
        }

        .dept-header h2 {
          font-family: 'Playfair Display', serif;
          font-size: 1.6rem;
          color: var(--text-primary);
          margin: 0;
        }

        .dept-meta {
          display: flex;
          gap: 20px;
          flex-wrap: wrap;
          margin-top: 4px;
        }

        .dept-meta span {
          font-size: 0.85rem;
          color: var(--text-secondary);
          display: flex;
          align-items: center;
          gap: 6px;
        }

        .dept-meta span i {
          color: var(--clr-blue);
        }

        /* Tabs */
        .dept-tabs {
          display: flex;
          gap: 4px;
          padding: 0 32px;
          background: rgba(255, 255, 255, 0.2);
          border-bottom: 1px solid rgba(14, 165, 233, 0.08);
          overflow-x: auto;
        }

        .dept-tabs button {
          padding: 14px 24px;
          background: none;
          border: none;
          cursor: pointer;
          font-weight: 700;
          font-size: 0.85rem;
          color: var(--text-secondary);
          transition: all 0.3s ease;
          position: relative;
          white-space: nowrap;
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .dept-tabs button:hover {
          color: var(--text-primary);
        }

        .dept-tabs button.active {
          color: var(--clr-blue);
        }

        .dept-tabs button.active::after {
          content: '';
          position: absolute;
          bottom: 0;
          left: 24px;
          right: 24px;
          height: 3px;
          background: linear-gradient(135deg, var(--clr-blue), #0284c7);
          border-radius: 3px 3px 0 0;
        }

        .dept-content {
          padding: 32px;
        }

        .tab-content {
          animation: fadeIn 0.3s ease;
        }

        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }

        /* Overview */
        .overview-grid {
          display: grid;
          grid-template-columns: 1fr 2fr;
          gap: 32px;
        }

        .overview-left {
          display: flex;
          flex-direction: column;
          gap: 20px;
        }

        .dept-image {
          width: 100%;
          height: 280px;
          object-fit: cover;
          border-radius: 12px;
          border: 2px solid rgba(255, 255, 255, 0.6);
        }

        .dept-contact {
          background: rgba(255, 255, 255, 0.3);
          padding: 16px;
          border-radius: 12px;
          border: 1px solid rgba(255, 255, 255, 0.4);
        }

        .dept-contact h4 {
          font-size: 0.95rem;
          color: var(--text-primary);
          margin-bottom: 12px;
        }

        .dept-contact h4 i {
          color: var(--clr-blue);
          margin-right: 8px;
        }

        .dept-contact p {
          font-size: 0.85rem;
          color: var(--text-secondary);
          margin: 6px 0;
        }

        .dept-contact p i {
          color: var(--clr-blue);
          width: 20px;
        }

        .overview-right h3 {
          font-family: 'Playfair Display', serif;
          font-size: 1.3rem;
          color: var(--text-primary);
          margin-bottom: 12px;
        }

        .overview-right > p {
          color: var(--text-secondary);
          line-height: 1.7;
          font-size: 0.95rem;
        }

        /* HOD Message with Profile */
        .hod-message {
          margin-top: 24px;
        }

        .hod-message h4 {
          font-size: 0.95rem;
          color: var(--text-primary);
          margin-bottom: 12px;
        }

        .hod-message h4 i {
          color: var(--clr-blue);
          margin-right: 8px;
        }

        .hod-message-box {
          background: rgba(255, 255, 255, 0.3);
          padding: 24px;
          border-radius: 12px;
          border-left: 4px solid var(--clr-blue);
        }

        .hod-profile {
          display: flex;
          align-items: center;
          gap: 20px;
          margin-bottom: 16px;
          padding-bottom: 16px;
          border-bottom: 1px solid rgba(14, 165, 233, 0.1);
        }

        .hod-avatar-large {
          width: 80px;
          height: 80px;
          border-radius: 50%;
          object-fit: cover;
          border: 3px solid var(--clr-blue);
          flex-shrink: 0;
        }

        .hod-info h5 {
          font-family: 'Playfair Display', serif;
          font-size: 1.1rem;
          color: var(--text-primary);
          margin-bottom: 2px;
        }

        .hod-designation {
          color: var(--clr-blue);
          font-weight: 600;
          font-size: 0.85rem;
          display: block;
          margin-bottom: 4px;
        }

        .hod-contact {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          font-size: 0.8rem;
          color: var(--text-secondary);
          margin-right: 12px;
        }

        .hod-contact i {
          color: var(--clr-blue);
        }

        .hod-message-text p {
          color: var(--text-secondary);
          font-size: 0.95rem;
          line-height: 1.7;
          font-style: italic;
          margin: 0;
        }

        /* Achievements */
        .achievements {
          margin-top: 24px;
        }

        .achievements h4 {
          font-size: 0.95rem;
          color: var(--text-primary);
          margin-bottom: 10px;
        }

        .achievements h4 i {
          color: var(--clr-amber);
          margin-right: 8px;
        }

        .achievement-tags {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
        }

        .achievement-tag {
          background: rgba(245, 158, 11, 0.12);
          color: var(--clr-amber);
          padding: 4px 14px;
          border-radius: 20px;
          font-size: 0.75rem;
          font-weight: 600;
          border: 1px solid rgba(245, 158, 11, 0.2);
        }

        /* Faculty Tab */
        .faculty-section h3 {
          font-family: 'Playfair Display', serif;
          font-size: 1.2rem;
          color: var(--text-primary);
          margin-bottom: 4px;
        }

        .faculty-count {
          color: var(--text-secondary);
          font-size: 0.9rem;
          margin-bottom: 20px;
        }

        .faculty-grid-full {
          display: flex;
          flex-direction: column;
          gap: 24px;
        }

        .faculty-card-full {
          background: rgba(255, 255, 255, 0.3);
          border-radius: 12px;
          padding: 20px;
          border: 1px solid rgba(255, 255, 255, 0.4);
        }

        .hod-card-full {
          border: 2px solid rgba(245, 158, 11, 0.3);
          background: rgba(245, 158, 11, 0.05);
        }

        .faculty-card-header {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 0.8rem;
          font-weight: 700;
          color: var(--text-secondary);
          margin-bottom: 12px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .faculty-card-content {
          display: flex;
          align-items: center;
          gap: 20px;
        }

        .faculty-card-content img {
          width: 60px;
          height: 60px;
          border-radius: 50%;
          object-fit: cover;
          border: 2px solid var(--clr-blue);
        }

        .faculty-card-content h4 {
          font-family: 'Playfair Display', serif;
          font-size: 1.1rem;
          color: var(--text-primary);
          margin-bottom: 2px;
        }

        .faculty-card-content p {
          color: var(--clr-blue);
          font-weight: 600;
          font-size: 0.85rem;
          margin-bottom: 4px;
        }

        .faculty-card-content span {
          display: block;
          font-size: 0.8rem;
          color: var(--text-secondary);
        }

        .faculty-card-content span i {
          color: var(--clr-blue);
          width: 16px;
        }

        .faculty-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
          gap: 16px;
        }

        .faculty-mini-card {
          background: rgba(255, 255, 255, 0.3);
          border-radius: 12px;
          padding: 16px;
          text-align: center;
          border: 1px solid rgba(255, 255, 255, 0.4);
          transition: all 0.3s ease;
        }

        .faculty-mini-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 15px rgba(0, 0, 0, 0.06);
        }

        .faculty-mini-card img {
          width: 60px;
          height: 60px;
          border-radius: 50%;
          object-fit: cover;
          margin-bottom: 8px;
        }

        .faculty-mini-card h5 {
          font-size: 0.85rem;
          color: var(--text-primary);
          font-weight: 700;
          margin: 4px 0;
        }

        .faculty-mini-card span {
          font-size: 0.75rem;
          color: var(--text-secondary);
        }

        .faculty-placeholder {
          grid-column: 1 / -1;
          text-align: center;
          padding: 40px;
          background: rgba(255, 255, 255, 0.2);
          border-radius: 12px;
          border: 1px dashed rgba(14, 165, 233, 0.2);
        }

        .faculty-placeholder i {
          font-size: 2.5rem;
          color: var(--clr-blue);
          display: block;
          margin-bottom: 12px;
        }

        .faculty-placeholder p {
          color: var(--text-secondary);
          margin-bottom: 12px;
        }

        .view-all-link {
          display: inline-block;
          padding: 10px 24px;
          background: linear-gradient(135deg, var(--clr-blue), #0284c7);
          color: white;
          border-radius: 30px;
          font-weight: 600;
          transition: all 0.3s ease;
        }

        .view-all-link:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 20px rgba(14, 165, 233, 0.3);
        }

        /* Notices */
        .notices-section h3 {
          font-family: 'Playfair Display', serif;
          font-size: 1.2rem;
          color: var(--text-primary);
          margin-bottom: 20px;
        }

        .notices-list {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .notice-item {
          display: flex;
          align-items: center;
          gap: 16px;
          padding: 16px 20px;
          background: rgba(255, 255, 255, 0.3);
          border-radius: 12px;
          border: 1px solid rgba(255, 255, 255, 0.4);
          transition: all 0.3s ease;
        }

        .notice-item:hover {
          background: rgba(255, 255, 255, 0.5);
          transform: translateX(5px);
        }

        .notice-icon {
          width: 44px;
          height: 44px;
          border-radius: 10px;
          background: rgba(244, 63, 94, 0.1);
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.2rem;
          color: #f43f5e;
          flex-shrink: 0;
        }

        .notice-content {
          flex: 1;
        }

        .notice-content h4 {
          font-size: 0.95rem;
          color: var(--text-primary);
          margin-bottom: 2px;
        }

        .notice-content p {
          font-size: 0.85rem;
          color: var(--text-secondary);
          margin: 4px 0;
        }

        .notice-date {
          font-size: 0.75rem;
          color: var(--text-muted);
          display: flex;
          align-items: center;
          gap: 6px;
        }

        .notice-download {
          padding: 8px 16px;
          background: rgba(14, 165, 233, 0.1);
          border: 1px solid rgba(14, 165, 233, 0.2);
          border-radius: 8px;
          color: var(--clr-blue);
          text-decoration: none;
          font-weight: 600;
          font-size: 0.8rem;
          transition: all 0.3s ease;
          white-space: nowrap;
        }

        .notice-download:hover {
          background: var(--clr-blue);
          color: white;
        }

        /* Labs */
        .labs-section h3 {
          font-family: 'Playfair Display', serif;
          font-size: 1.2rem;
          color: var(--text-primary);
          margin-bottom: 20px;
        }

        .labs-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
          gap: 20px;
        }

        .lab-card {
          background: rgba(255, 255, 255, 0.3);
          border-radius: 12px;
          overflow: hidden;
          border: 1px solid rgba(255, 255, 255, 0.4);
          transition: all 0.3s ease;
        }

        .lab-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 8px 25px rgba(0, 0, 0, 0.08);
        }

        .lab-images {
          display: flex;
          height: 150px;
          overflow: hidden;
        }

        .lab-image {
          flex: 1;
          object-fit: cover;
        }

        .more-images {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 60px;
          background: rgba(0, 0, 0, 0.5);
          color: white;
          font-weight: 700;
          font-size: 0.9rem;
        }

        .lab-info {
          padding: 16px;
        }

        .lab-info h4 {
          font-family: 'Playfair Display', serif;
          font-size: 1rem;
          color: var(--text-primary);
          margin-bottom: 4px;
        }

        .lab-equipment {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 0.8rem;
          color: var(--text-secondary);
          background: rgba(14, 165, 233, 0.06);
          padding: 6px 12px;
          border-radius: 6px;
          margin: 8px 0;
        }

        .lab-equipment i {
          color: var(--clr-blue);
        }

        .lab-info p {
          color: var(--text-secondary);
          font-size: 0.85rem;
          line-height: 1.5;
          margin: 8px 0 0;
        }

        /* Gallery */
        .gallery-section h3 {
          font-family: 'Playfair Display', serif;
          font-size: 1.2rem;
          color: var(--text-primary);
          margin-bottom: 20px;
        }

        .gallery-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
          gap: 16px;
        }

        .gallery-item {
          position: relative;
          border-radius: 12px;
          overflow: hidden;
          cursor: pointer;
          aspect-ratio: 1;
        }

        .gallery-item img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: transform 0.3s ease;
        }

        .gallery-item:hover img {
          transform: scale(1.05);
        }

        /* Empty States */
        .empty-state {
          text-align: center;
          padding: 60px 20px;
          background: rgba(255, 255, 255, 0.45);
          backdrop-filter: blur(20px) saturate(140%);
          border-radius: 16px;
          border: 1px solid rgba(255, 255, 255, 0.6);
        }

        .empty-state i {
          font-size: 3.5rem;
          color: var(--text-muted);
          margin-bottom: 16px;
          display: block;
        }

        .empty-state h3 {
          font-family: 'Playfair Display', serif;
          font-size: 1.3rem;
          color: var(--text-primary);
          margin-bottom: 6px;
        }

        .empty-state p {
          color: var(--text-secondary);
          font-size: 0.95rem;
        }

        .empty-state-mini {
          text-align: center;
          padding: 40px;
          color: var(--text-muted);
        }

        .empty-state-mini i {
          font-size: 2.5rem;
          display: block;
          margin-bottom: 12px;
          color: var(--text-muted);
        }

        .empty-state-mini p {
          font-size: 0.9rem;
        }

        /* Responsive */
        @media (max-width: 1024px) {
          .overview-grid {
            grid-template-columns: 1fr;
          }
          
          .stats-grid {
            grid-template-columns: repeat(2, 1fr);
          }
        }

        @media (max-width: 768px) {
          .departments-frontend {
            padding-top: 20px;
          }

          .page-title {
            font-size: 1.8rem;
          }

          .stats-grid {
            grid-template-columns: repeat(2, 1fr);
            gap: 12px;
          }

          .stat-card {
            padding: 16px 12px;
          }

          .stat-value {
            font-size: 1.5rem;
          }

          .stat-label {
            font-size: 0.7rem;
          }

          .dept-selector {
            padding: 12px;
            gap: 8px;
          }

          .dept-btn {
            padding: 8px 16px;
            font-size: 0.8rem;
          }

          .dept-header {
            padding: 16px 20px;
            flex-direction: column;
            align-items: stretch;
          }

          .dept-header-left {
            flex-direction: column;
            align-items: flex-start;
          }

          .dept-tabs {
            padding: 0 16px;
          }

          .dept-tabs button {
            padding: 10px 14px;
            font-size: 0.75rem;
          }

          .dept-content {
            padding: 16px 20px;
          }

          .dept-image {
            height: 200px;
          }

          .hod-profile {
            flex-direction: column;
            text-align: center;
          }

          .hod-avatar-large {
            width: 70px;
            height: 70px;
          }

          .faculty-card-content {
            flex-direction: column;
            text-align: center;
          }

          .labs-grid {
            grid-template-columns: 1fr;
          }

          .gallery-grid {
            grid-template-columns: repeat(2, 1fr);
          }

          .notice-item {
            flex-wrap: wrap;
          }

          .notice-download {
            width: 100%;
            text-align: center;
          }

          .faculty-grid {
            grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
          }
        }

        @media (max-width: 480px) {
          .page-title {
            font-size: 1.5rem;
          }

          .stats-grid {
            grid-template-columns: 1fr 1fr;
            gap: 10px;
          }

          .stat-card {
            padding: 12px 10px;
          }

          .stat-value {
            font-size: 1.2rem;
          }

          .stat-label {
            font-size: 0.6rem;
          }

          .stat-icon {
            width: 40px;
            height: 40px;
            font-size: 1rem;
          }

          .dept-selector {
            flex-direction: column;
          }

          .dept-btn {
            width: 100%;
            justify-content: center;
          }

          .dept-tabs {
            gap: 2px;
          }

          .dept-tabs button {
            padding: 8px 12px;
            font-size: 0.65rem;
          }

          .gallery-grid {
            grid-template-columns: 1fr 1fr;
            gap: 10px;
          }

          .faculty-grid {
            grid-template-columns: 1fr 1fr;
          }

          .hod-avatar-large {
            width: 60px;
            height: 60px;
          }
        }
      `}</style>
    </div>
  );
};

export default DepartmentFrontend;